import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon, ZapIcon } from "lucide-react";
import { useState } from "react";
import { fadeUp } from "~/lib/animations";
import { getSignalConfig } from "~/pages/notifications/notifications.config";
import type { Signal } from "~/pages/notifications/notifications.types";
import { formatTime } from "~/pages/notifications/notifications.utils";

interface SignalStripProps {
  signals: Signal[];
}

export function SignalStrip({ signals }: Readonly<SignalStripProps>) {
  const [collapsed, setCollapsed] = useState(false);
  const actionable = signals.filter((s) => s.actionable && !s.readAt);
  const now = new Date();

  if (actionable.length === 0) return null;

  return (
    <motion.div {...fadeUp}>
      <Card className="border-signal-500/30 bg-signal-50/50 dark:border-signal-500/20 dark:bg-signal-900/20">
        <CardContent className="px-4 py-3">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2"
            onClick={() => setCollapsed((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <div className="flex size-6 items-center justify-center rounded-md bg-signal-500">
                <ZapIcon className="size-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold text-foreground">
                {actionable.length} {actionable.length === 1 ? "signal needs" : "signals need"} your response
              </span>
            </div>
            {collapsed ? (
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            ) : (
              <ChevronUpIcon className="size-4 text-muted-foreground" />
            )}
          </button>

          {!collapsed && (
            <div className="mt-3 space-y-2">
              {actionable.slice(0, 3).map((s) => {
                const cfg = getSignalConfig(s.type);
                const SIcon = cfg.Icon;
                return (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg bg-card/60 px-3 py-2.5">
                    <div className={cn("flex size-7 shrink-0 items-center justify-center rounded-md", cfg.iconBg)}>
                      <SIcon className={cn("size-3.5", cfg.iconColor)} />
                    </div>
                    <p className="min-w-0 flex-1 truncate text-sm font-medium">{s.title}</p>
                    <span className="shrink-0 text-[0.6875rem] text-muted-foreground">
                      {formatTime(s.createdAt, now)}
                    </span>
                    {s.type === "crew_request" && (
                      <div className="flex gap-1.5">
                        <Button size="sm" className="h-6 px-2.5 text-xs">
                          Accept
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                          Decline
                        </Button>
                      </div>
                    )}
                    {s.type === "payment_failed" && (
                      <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                        Retry
                      </Button>
                    )}
                    {s.type === "cert_expiring" && (
                      <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                        Remind
                      </Button>
                    )}
                  </div>
                );
              })}
              {actionable.length > 3 && (
                <p className="text-center text-xs text-muted-foreground">
                  +{actionable.length - 3} more waiting for your response
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
